import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild
} from "@angular/core";
import { DxFormComponent } from "devextreme-angular";
import { LoadOptions } from "devextreme/data";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import { isNumber } from "lodash";
import { Address } from "src/app/backend/models/address";
import {
  IndirizzoService,
  IndirizzoServiceCity,
  IndirizzoServiceSearchResult
} from "src/app/services/indirizzo.service";
import { Bind } from "src/app/support/decorators/bind";
import { sntTranslate } from "src/app/support/translate";
import { ModuleService } from "src/app/services/feature-access-control/module.service";
import { MODULES } from "src/app/core/modules";
import { RegionCode } from "google-libphonenumber";

type FormData = {
  country?: string | undefined;
  address?: string | undefined;
  zipCode?: string | undefined;
  state?: string | undefined;
  city?: string | undefined;
};

@Component({
  selector: "snt-editable-address",
  templateUrl: "editable-address.component.html",
  styles: [
    `
      :host {
        display: flex;
        flex: 1;
      }
    `
  ],
  standalone: false
})
export class EditableAddressComponent implements OnInit {
  @ViewChild(DxFormComponent)
  dxForm!: DxFormComponent;

  @Output()
  addressSelected: EventEmitter<FormData> = new EventEmitter();

  addressCountryDataSource: DataSource;
  addressStateDataSource: DataSource;
  addressCityDataSource: DataSource;
  addressZipCodeDataSource: DataSource;

  formData: FormData = {
    country: "IT"
  };

  isDistrictIncluded = inject(ModuleService).canBeActivatedSync(
    MODULES.PROVINCIA_CLIENTE
  );

  private readonly indirizzoService = inject(IndirizzoService);

  @Input()
  address!: Address;

  @Input()
  dropDownOptions?: {
    container?: HTMLElement;
    zIndex?: number;
  };

  region = signal<RegionCode>(<RegionCode>"IT");

  constructor() {
    this.addressCountryDataSource = new DataSource({
      paginate: true,
      store: new CustomStore({
        byKey: (code: string) => {
          return Promise.resolve(
            this.indirizzoService.getCoountries().find((x) => x.code === code)!
          ).then((result) => ({
            code: result.code,
            name: sntTranslate(result.name)
          }));
        },
        load: async (loadOptions: LoadOptions) => {
          let data = await this.indirizzoService.getCoountries().map((x) => ({
            code: x.code,
            name: sntTranslate(x.name)
          }));

          if (
            loadOptions.searchOperation === "contains" &&
            loadOptions.searchValue
          ) {
            const query = loadOptions.searchValue.toLowerCase();
            data = data.filter((x) => x.name.toLowerCase().indexOf(query) >= 0);
          }

          if (isNumber(loadOptions.skip)) {
            data = data.slice(
              loadOptions.skip,
              loadOptions.skip + loadOptions.take!
            );
          }

          return data;
        }
      })
    });

    this.addressStateDataSource = new DataSource({
      paginate: true,
      store: new CustomStore({
        byKey: (code: string) =>
          Promise.resolve(
            this.indirizzoService.getProvince().find((x) => x.code === code)!
          ),
        load: async (loadOptions: LoadOptions) => {
          let data = await this.indirizzoService.getProvince();

          if (
            loadOptions.searchOperation === "contains" &&
            loadOptions.searchValue
          ) {
            const query = loadOptions.searchValue.toLowerCase();
            data = data.filter((x) => x.name.toLowerCase().indexOf(query) >= 0);
          }

          if (isNumber(loadOptions.skip)) {
            data = data.slice(
              loadOptions.skip,
              loadOptions.skip + loadOptions.take!
            );
          }

          return data;
        }
      })
    });

    this.addressZipCodeDataSource = new DataSource({
      paginate: true,
      store: new CustomStore({
        byKey: async (key: string) => {
          return {
            code: key
          };
        },
        load: async (loadOptions: LoadOptions) => {
          const region: RegionCode = this.region();

          let data = await (region === "CH"
            ? this.indirizzoService.getCapCH()
            : this.indirizzoService.getCap());

          if (
            loadOptions.searchOperation === "contains" &&
            loadOptions.searchValue
          ) {
            const query = loadOptions.searchValue.toLowerCase();
            data = data.filter((x) => x.code.toLowerCase().indexOf(query) >= 0);
          }

          if (isNumber(loadOptions.skip)) {
            data = data.slice(
              loadOptions.skip,
              loadOptions.skip + loadOptions.take!
            );
          }

          return data;
        }
      })
    });

    this.addressCityDataSource = new DataSource({
      paginate: true,
      store: new CustomStore({
        byKey: async (key: string) => {
          return {
            name: key
          };
        },
        load: async (loadOptions: LoadOptions) => {
          const region: RegionCode = this.region();

          let data: Array<{ name: string }> =
            await this.indirizzoService.getComuni(region);

          if (
            loadOptions.searchOperation === "contains" &&
            loadOptions.searchValue
          ) {
            const query = loadOptions.searchValue.toLowerCase();
            data = data.filter((x) => x.name.toLowerCase().indexOf(query) >= 0);
          }

          if (isNumber(loadOptions.skip)) {
            data = data.slice(
              loadOptions.skip,
              loadOptions.skip + loadOptions.take!
            );
          }

          return data;
        }
      })
    });
  }

  ngOnInit(): void {
    this.formData = {
      ...this.formData,
      address: this.address?.address,
      city: this.address?.city,
      country: this.address?.country || this.formData.country,
      state: this.address?.state,
      zipCode: this.address?.zipCode
    };

    this.region.set(<RegionCode>this.formData.country);
  }

  onFieldDataChanged(): void {
    const currentRegion: RegionCode = <RegionCode>this.formData.country;

    this.region.set(currentRegion);

    if (currentRegion === "CH") {
      this.formData.state = undefined;
    }

    this.addressSelected.emit(this.formData);
  }

  // @Bind
  // onAddressAddressSelectionChanged({
  //   selectedItem
  // }: {
  //   selectedItem: IndirizzoServiceSearchResult;
  // }): void {
  //   setTimeout(() => {
  //     if (selectedItem) {
  //       this.dxForm.instance.updateData({
  //         address: selectedItem.formattedRoute,
  //         city: selectedItem.city,
  //         zipCode: selectedItem.postalCode,
  //         state: selectedItem.province
  //       });
  //     }
  //   });
  // }

  getFieldTypeByNation(): string {
    const typeMap: Partial<Record<RegionCode, string>> = {
      IT: "dxSelectBox",
      CH: "dxAutocomplete"
    };

    let fieldType: string = "dxTextBox";
    let region: RegionCode = this.region();

    if (Object.keys(typeMap).includes(this.region())) {
      fieldType = typeMap[region]!;
    }

    return fieldType;
  }
}
